import { z } from 'zod';
import { IWorkspacePlanModel } from 'api/models/response/workspacePlan/IWorkspacePlanModel';
import { ISaveWorkspacePlanRequest } from 'api/models/request/workspacePlan/ISaveWorkspacePlanRequest';

export const BYTES_IN_GB = 1024 ** 3;
export const UNLIMITED = -1;

const MAX_NAME_LENGTH = 200;
const MAX_CODE_LENGTH = 100;

export type WorkspacePlanFormValues = {
    name: string;
    code: string;
    storageLimitGb: string;
    maxUsers: string;
    maxGogglesCount: string;
};

export const EMPTY_PLAN_FORM: WorkspacePlanFormValues = {
    name: '',
    code: '',
    storageLimitGb: '',
    maxUsers: '',
    maxGogglesCount: '',
};

const isLimitValid = (value: string): boolean => {
    const parsed = Number(value);
    return value.trim() !== '' && Number.isInteger(parsed) && parsed >= UNLIMITED;
};

const isStorageValid = (value: string): boolean => {
    const parsed = Number(value);
    return value.trim() !== '' && !Number.isNaN(parsed) && parsed >= 0;
};

export const createWorkspacePlanSchema = (t: (key: string, options?: Record<string, unknown>) => string) => z.object({
    name: z
        .string()
        .refine((value) => value.trim() !== '', { message: t('subscriptionPlan.validation.nameRequired') })
        .refine((value) => value.trim().length <= MAX_NAME_LENGTH, { message: t('subscriptionPlan.validation.nameTooLong') }),
    code: z
        .string()
        .refine((value) => value.trim().length <= MAX_CODE_LENGTH, { message: t('subscriptionPlan.validation.codeTooLong') }),
    storageLimitGb: z
        .string()
        .refine(isStorageValid, { message: t('subscriptionPlan.validation.storageInvalid') }),
    maxUsers: z
        .string()
        .refine(isLimitValid, { message: t('subscriptionPlan.validation.limitInvalid') }),
    maxGogglesCount: z
        .string()
        .refine(isLimitValid, { message: t('subscriptionPlan.validation.limitInvalid') }),
});

export const bytesToGbString = (bytes: number): string => String(Number((bytes / BYTES_IN_GB).toFixed(2)));

export const formatBytes = (bytes: number): string => {
    if (bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${Number((bytes / 1024 ** exponent).toFixed(2))} ${units[exponent]}`;
};

export const planToFormValues = (plan: IWorkspacePlanModel): WorkspacePlanFormValues => ({
    name: plan.name,
    code: plan.code,
    storageLimitGb: bytesToGbString(plan.storageLimitBytes),
    maxUsers: String(plan.maxUsers),
    maxGogglesCount: String(plan.maxGogglesCount),
});


const storageGbToBytes = (storageLimitGb: string, originalPlan?: IWorkspacePlanModel): number => {
    if (originalPlan && Number(storageLimitGb) === Number(bytesToGbString(originalPlan.storageLimitBytes))) {
        return originalPlan.storageLimitBytes;
    }
    return Math.round(Number(storageLimitGb) * BYTES_IN_GB);
};

export const formValuesToRequest = (
    values: WorkspacePlanFormValues,
    originalPlan?: IWorkspacePlanModel,
): ISaveWorkspacePlanRequest => ({
    name: values.name.trim(),
    code: values.code.trim() || null,
    storageLimitBytes: storageGbToBytes(values.storageLimitGb, originalPlan),
    maxUsers: Number(values.maxUsers),
    maxGogglesCount: Number(values.maxGogglesCount),
});
